import { formatIsoEnZona } from '@/shared/lib/dateUtils'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useMemo, useRef, useEffect } from "react";
import { CamposDeDesenlace, type Desenlace } from "@/features/almacen/components/CamposDeDesenlace";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreVertical,
  Eye,
  X,
  Loader2,
  CirclePlus,
} from "lucide-react";
import { apiService } from '@/services/core/api.service';
import { useAuth } from '@/shared/hooks/useAuth';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { ALMACEN } from '@/constants/permissions';
import ReporteDetailView from '@/features/almacen/components/ReporteDetailView';
import { ReporteModal } from '@/features/almacen/components/HerramientaForm';
import type { Reporte } from '@/features/almacen/components/ReporteDetailView';
import toast from "react-hot-toast";
import FilterSelect from '@/shared/components/FilterSelect';
import { EsqueletoTabla } from "@/shared/components/Esqueleto";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';
import { Badge, type ColorDeBadge } from "@/shared/components/Badge";

// ─── API types ────────────────────────────────────────────────────────────────

type EstadoReporte  = "Falla/daño" | "Faltante";
type EstatusReporte = "Abierto" | "Cerrado";

interface ApiReporte {
  id: string;
  folio: number;
  folioDisplay: string;
  toolId: string;
  toolName: string | null;
  serialCode?: string | null;
  internalCode?: string | null;
  type: string;
  status: string;
  projectId: string;
  projectName: string | null;
  reportedByUserId: string;
  // El back resuelve el nombre desde su relación con el usuario. Antes esta
  // pantalla lo cruzaba con /users/public/all, que solo trae activos: si el
  // usuario que reportó o cerró se desactivaba, se perdía la trazabilidad.
  reportedByUserName: string | null;
  incidentComment: string | null;
  resolutionComment: string | null;
  closedByUserId: string | null;
  closedByUserName: string | null;
  closedAt: string | null;
  createdAt: string;
  evidenceUrl1?: string | null;
  evidenceUrl2?: string | null;
  evidenceUrl3?: string | null;
}

interface PaginatedReportes {
  data: ApiReporte[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

// Filas por página en la tabla. Los controles solo aparecen si hay más.
const PAGE_SIZE = 10;
// Tamaño de página al pedirle al API. No es un tope: se recorren todas las
// páginas con el totalPages que devuelve, solo evita hacer una petición por
// cada 20 registros.
const FETCH_PAGE_SIZE = 100;

// ─── Mapping ──────────────────────────────────────────────────────────────────

function mapTipo(type: string): EstadoReporte {
  if (type === "missing")      return "Faltante";
  return "Falla/daño";
}

function mapApiReporte(r: ApiReporte): Reporte {
  return {
    id:          r.id,
    folio:       r.folioDisplay,
    toolId:      r.toolId,
    herramienta: r.toolName ?? "Sin herramienta",
    codigoSerie: r.serialCode ?? "Sin número de serie",
    internalCode: r.internalCode ?? "Sin código interno",
    proyecto:    r.projectName ?? "Sin proyecto",
    responsable: r.reportedByUserName ?? "Sin responsable",
    fechaHora:   formatIsoEnZona(r.createdAt),
    createdAtISO: r.createdAt,
    estado:      mapTipo(r.type),
    estatus:     r.status === "closed" ? "Cerrado" : "Abierto",
    descripcion: r.incidentComment ?? "",
    resolucion:  r.resolutionComment ?? "",
    cerradoPor:  r.closedByUserId ? (r.closedByUserName ?? "Sin registro") : undefined,
    fechaCierre: r.closedAt ? formatIsoEnZona(r.closedAt) : undefined,
    closedAtISO: r.closedAt ?? undefined,
    fotos:       [r.evidenceUrl1, r.evidenceUrl2, r.evidenceUrl3].filter((u): u is string => !!u),
  };
}

// ─── Actions menu ─────────────────────────────────────────────────────────────

const ActionsMenu = ({
  onVer,
  onCerrar,
}: {
  onVer?: () => void;
  onCerrar?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current && !btnRef.current.closest("[data-actions-menu]")?.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const items = [
    { label: "Ver",    icon: Eye, action: onVer    },
    { label: "Cerrar", icon: X,   action: onCerrar },
  ].filter((item) => item.action !== undefined);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      // Si no cabe hacia abajo (filas al final de la tabla), se abre hacia
      // arriba en vez de quedar cortado por el borde de la ventana.
      const estimatedMenuHeight = items.length * 40 + 8;
      const opensUpward = window.innerHeight - rect.bottom < estimatedMenuHeight + 4;
      setMenuPos({
        top: opensUpward ? rect.top - estimatedMenuHeight - 4 : rect.bottom + 4,
        left: rect.right - 144,
      });
    }
    setOpen((v) => !v);
  };

  return (
    <div data-actions-menu="">
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <MoreVertical className="w-4 h-4" style={{ color: "#315FB9" }} />
      </button>
      {open && (
        <div
          className="fixed w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] py-1"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {items.map(({ label, icon: Icon, action }) => (
            <button
              key={label}
              onClick={() => { action?.(); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Icon className="w-4 h-4" style={{ color: "#315FB9" }} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Paginación ───────────────────────────────────────────────────────────────

const Pagination = ({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (p: number) => void }) => {
  const pages = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3)       return [1, 2, 3, "...", totalPages];
    if (page >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", page - 1, page, page + 1, "...", totalPages];
  }, [page, totalPages]);

  const btn = "inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm transition-colors";

  return (
    <div className="flex items-center justify-center gap-1 py-4">
      <button onClick={() => onPage(1)} disabled={page === 1} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronsLeft className="w-4 h-4" /></button>
      <button onClick={() => onPage(page - 1)} disabled={page === 1} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronLeft className="w-4 h-4" /></button>
      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`e-${i}`} className="w-8 h-8 inline-flex items-center justify-center text-sm text-gray-400">...</span>
        ) : (
          <button key={p} onClick={() => onPage(p as number)}
            className={`${btn} font-medium ${page === p ? "text-white" : "text-gray-600 hover:bg-gray-100"}`}
            style={page === p ? { backgroundColor: "#253579" } : undefined}
          >{p}</button>
        )
      )}
      <button onClick={() => onPage(page + 1)} disabled={page === totalPages} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronRight className="w-4 h-4" /></button>
      <button onClick={() => onPage(totalPages)} disabled={page === totalPages} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronsRight className="w-4 h-4" /></button>
    </div>
  );
};

// Estado en el que puede quedar la herramienta al cerrar la incidencia. Es
// opcional: cerrar sin elegir deja la herramienta como esta. La baja no esta
// aqui porque tiene su propio flujo, con motivo y evidencia.
// ─── Cerrar reporte modal ─────────────────────────────────────────────────────

const CerrarReporteModal = ({
  reporte,
  comentario,
  onChangeComentario,
  onChangeDesenlace,
  onSubiendoChange,
  puedeCerrar,
  saving,
  onClose,
  onConfirm,
}: {
  reporte: Reporte;
  comentario: string;
  onChangeComentario: (v: string) => void;
  onChangeDesenlace: (d: Desenlace | null) => void;
  onSubiendoChange: (subiendo: boolean) => void;
  puedeCerrar: boolean;
  saving: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <CapaDeModal>
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
      <div className="flex items-start justify-between px-6 pt-6 pb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Cerrar reporte</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Cierra la incidencia &ldquo;{reporte.folio}&rdquo; de {reporte.herramienta}.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="text-gray-400 hover:text-gray-600 transition-colors ml-4 mt-0.5 disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="px-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nota de resolución
        </label>
        <textarea
          rows={4}
          placeholder="Describe cómo se resolvió la incidencia (opcional)"
          value={comentario}
          onChange={(e) => onChangeComentario(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B5998] resize-none"
        />

        <div className="mt-4">
          <CamposDeDesenlace
            onChange={onChangeDesenlace}
            onSubiendoChange={onSubiendoChange}
            esFaltante={reporte.estado === "Faltante"}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 px-6 py-5">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className={estiloDeBoton('secundario')}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          // Sin desenlace elegido no se puede cerrar: es el criterio de ALM-01.
          disabled={saving || !puedeCerrar}
          className={estiloDeBoton('primario')}
        >
          {saving ? "Cerrando…" : "Cerrar reporte"}
        </button>
      </div>
    </div>
  </CapaDeModal>
);

// ─── Badges ───────────────────────────────────────────────────────────────────

const EstadoBadge = ({ estado }: { estado: EstadoReporte }) => {
  const colores: Record<EstadoReporte, ColorDeBadge> = {
    "Falla/daño": "rojo",
    "Faltante": "amarillo",
  };
  return <Badge color={colores[estado]}>{estado}</Badge>;
};

const EstatusBadge = ({ estatus }: { estatus: EstatusReporte }) => {
  const colores: Record<EstatusReporte, ColorDeBadge> = {
    Abierto: "azul",
    Cerrado: "gris",
  };
  return <Badge color={colores[estatus]}>{estatus}</Badge>;
};

// ─── Empty states ─────────────────────────────────────────────────────────────

const NoResultsState = () => (
  <div className="flex flex-col items-center justify-center py-16 gap-2">
    <p className="text-sm font-medium text-gray-500">Sin resultados</p>
    <p className="text-sm text-gray-400">Intenta con otros filtros.</p>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const ReportesActivos = () => {
  const [reportes, setReportes]                   = useState<Reporte[]>([]);
  const [loading, setLoading]                     = useState(true);
  // Cerrar rapido desde el listado (sin entrar al detalle) — antes cerraba
  // con resolutionComment vacio sin darle chance al usuario de escribir nada.
  const [cerrarTarget, setCerrarTarget]            = useState<Reporte | null>(null);
  const [cerrarComentario, setCerrarComentario]    = useState("");
  // Estado con el que queda la herramienta al cerrar. Vacio = no se toca.
  const [cerrarDesenlace, setCerrarDesenlace]      = useState<Desenlace | null>(null);
  const [subiendoEvidencia, setSubiendoEvidencia]  = useState(false);
  const [cerrando, setCerrando]                    = useState(false);
  const [search, setSearch]                       = useState("");
  // Al entrar, solo se muestran las incidencias que requieren accion.
  const [filterEstatus, setFilterEstatus]         = useState("Abierto");
  const [filterTipo, setFilterTipo]               = useState("");
  const [filterProyecto, setFilterProyecto]       = useState("");
  const [filterResponsable, setFilterResponsable] = useState("");
  const [filterFecha, setFilterFecha]             = useState("");
  const [page, setPage]                           = useState(1);

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, isLoading: permsLoading } = usePermissions();
  // Sin permiso de cerrar incidencias, el usuario solo levanta las suyas: se
  // le acota el listado igual que en la app móvil.
  const soloPropios = !permsLoading && !hasPermission(ALMACEN.REPORTES_CERRAR);
  const canCrear = !permsLoading && hasPermission(ALMACEN.REPORTES_CREAR);
  // El alta vive en su propia URL para que el acceso directo desde el
  // dashboard funcione y el boton Atras del navegador la cierre.
  const creando = location.pathname === "/almacen/reportes/nuevo";

  // El detalle vive en su propia URL (/almacen/reportes/:id/ver) para que
  // el boton Atras del navegador y el reload funcionen como se espera, en
  // vez de depender de un estado local que se pierde al refrescar.
  // El id lo entrega el router desde la ruta /almacen/reportes/:id/ver, en vez
  // de leerlo de la URL con una expresion regular escrita a mano.
  const { id: selectedIdParam } = useParams<{ id: string }>();
  const selectedId = selectedIdParam ?? null;
  const selected = selectedId ? reportes.find((r) => r.id === selectedId) ?? null : null;

  // ── Fetch ──
  const fetchReportes = async (): Promise<Reporte[]> => {
    try {
      setLoading(true);
      // Quien puede cerrar incidencias las gestiona todas; el resto solo ve las
      // que levantó, igual que la app móvil. Es un filtro de cliente: el
      // endpoint no acota por usuario, así que oculta pero no restringe.
      const base = soloPropios && user?.id
        ? `reportedByUserId=${user.id}&`
        : "";
      const pageUrl = (page: number) =>
        `/almacen/reportes?${base}page=${page}&limit=${FETCH_PAGE_SIZE}`;

      // Se traen todas las páginas, no las primeras N: como el listado viene
      // de la más reciente a la más vieja, un tope fijo dejaba fuera las
      // incidencias abiertas antiguas, que son las que más urge atender.
      const primera = await apiService.request<PaginatedReportes>(pageUrl(1));
      const totalPages = primera.meta?.totalPages ?? 1;
      const restantes = await Promise.all(
        Array.from({ length: Math.max(0, totalPages - 1) }, (_, i) =>
          apiService.request<PaginatedReportes>(pageUrl(i + 2)),
        ),
      );

      const todos = [primera, ...restantes].flatMap((res) => res.data ?? []);
      const mapped = todos.map((r) => mapApiReporte(r));
      setReportes(mapped);
      return mapped;
    } catch (err: unknown) {
      const e = err as { message?: string | string[] };
      const msg = Array.isArray(e.message) ? e.message.join(" ") : (e.message ?? "Error al cargar reportes");
      toast.error(msg);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Se espera a que resuelvan los permisos: si se pidiera antes, el usuario
    // acotado alcanzaría a ver la lista completa por un instante.
    if (!permsLoading) fetchReportes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permsLoading, soloPropios, user?.id]);

  // ── Unique filter options (derived from fetched data) ──
  const uniqueProyectos    = useMemo(() => [...new Set(reportes.map((r) => r.proyecto).filter((v) => v !== "Sin proyecto"))], [reportes]);
  const uniqueResponsables = useMemo(() => [...new Set(reportes.map((r) => r.responsable).filter((v) => v !== "Sin responsable"))], [reportes]);

  const handleLimpiar = () => {
    setSearch("");
    setFilterEstatus("");
    setFilterTipo("");
    setFilterProyecto("");
    setFilterResponsable("");
    setFilterFecha("");
  };

  const rows = useMemo(
    () =>
      reportes.filter((r) => {
        const q = search.toLowerCase();
        const matchSearch =
          !q ||
          r.herramienta.toLowerCase().includes(q) ||
          r.responsable.toLowerCase().includes(q) ||
          r.proyecto.toLowerCase().includes(q);

        const matchFecha =
          !filterFecha ||
          r.fechaHora.startsWith(filterFecha.replace(/-/g, "/")) ||
          r.fechaHora.startsWith(filterFecha);

        return (
          matchSearch &&
          (!filterEstatus     || r.estatus     === filterEstatus) &&
          (!filterTipo        || r.estado      === filterTipo) &&
          (!filterProyecto    || r.proyecto    === filterProyecto) &&
          (!filterResponsable || r.responsable === filterResponsable) &&
          matchFecha
        );
      }),
    [reportes, search, filterEstatus, filterTipo, filterProyecto, filterResponsable, filterFecha],
  );

  // Al cambiar un filtro el resultado es otro: quedarse en la página 5 de la
  // búsqueda anterior deja la tabla vacía sin motivo aparente.
  useEffect(() => {
    setPage(1);
  }, [search, filterEstatus, filterTipo, filterProyecto, filterResponsable, filterFecha]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // ── Close reporte ──
  const handleCerrar = async (id: string, comentario: string, desenlace: Desenlace) => {
    await apiService.request(`/almacen/reportes/${id}/cerrar`, {
      method: "POST",
      // El desenlace es obligatorio: cada uno lleva lo suyo —el estado, el
      // taller o el motivo de baja— y el back valida que venga completo.
      body: JSON.stringify({ resolutionComment: comentario, ...desenlace }),
    });
    // Recargamos del backend para traer los datos autoritativos del cierre
    // (resolución, cerrado por y fecha de cierre); el detalle se sincroniza
    // solo porque `selected` se deriva de `reportes` + el id de la URL.
    await fetchReportes();
  };

  const handleConfirmCerrarRapido = async () => {
    if (!cerrarTarget) return;
    setCerrando(true);
    try {
      if (!cerrarDesenlace) return;
      await handleCerrar(cerrarTarget.id, cerrarComentario.trim(), cerrarDesenlace);
      toast.success("Reporte cerrado");
      setCerrarTarget(null);
      setCerrarComentario("");
      setCerrarDesenlace(null);
    } catch {
      toast.error("Error al cerrar el reporte");
    } finally {
      setCerrando(false);
    }
  };

  // ── Detail view ──
  if (selectedId) {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      );
    }
    if (!selected) {
      return (
        <div className="mx-auto px-8 py-8">
          <p className="text-sm text-gray-500">No se encontró el reporte.</p>
          <button
            onClick={() => navigate("/almacen/reportes")}
            className="mt-2 text-sm font-semibold text-[#315FB9] hover:underline"
          >
            Volver al listado
          </button>
        </div>
      );
    }
    return (
      <ReporteDetailView
        reporte={selected}
        onBack={() => navigate("/almacen/reportes")}
        onCerrar={handleCerrar}
      />
    );
  }

  return (
    <div className="mx-auto px-8 py-8">

      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Incidencias</h1>
          <p className="text-sm text-gray-500 mt-1">
            Reportes de daño, extravío o falla pendientes de resolución
          </p>
        </div>
        {canCrear && (
          <button
            onClick={() => navigate("/almacen/reportes/nuevo")}
            className={estiloDeBoton('primario')}
          >
            <CirclePlus className="w-4 h-4" />
            Reportar incidencia
          </button>
        )}
      </div>

      {creando && (
        <ReporteModal
          onClose={() => navigate("/almacen/reportes")}
          onCreated={(reportId) => navigate(`/almacen/reportes/${reportId}/ver`)}
        />
      )}

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
        <div className="relative w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar en reportes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
          />
        </div>
        <FilterSelect
          label="Estado"
          options={(["Abierto", "Cerrado"]).map((o) => ({ value: o, label: o }))}
          value={filterEstatus}
          onChange={setFilterEstatus}
        ancho="contenido"
          />
        <FilterSelect
          label="Tipo"
          options={(["Falla/daño", "Faltante"]).map((o) => ({ value: o, label: o }))}
          value={filterTipo}
          onChange={setFilterTipo}
        ancho="contenido"
          />
        <FilterSelect
          label="Proyecto"
          options={(uniqueProyectos).map((o) => ({ value: o, label: o }))}
          value={filterProyecto}
          onChange={setFilterProyecto}
        ancho="contenido"
          />
        <FilterSelect
          label="Reportado por"
          options={(uniqueResponsables).map((o) => ({ value: o, label: o }))}
          value={filterResponsable}
          onChange={setFilterResponsable}
        ancho="contenido"
          />
        <div className="relative">
          <input
            type="date"
            value={filterFecha}
            onChange={(e) => setFilterFecha(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3B5998] w-[160px] date-brand"
            style={{ colorScheme: "light" }}
          />
        </div>
        <button
          onClick={handleLimpiar}
          className="text-sm font-semibold text-[#315FB9] hover:underline"
        >
          Limpiar
        </button>
      </div>

      {/* Loading */}
      {loading ? (
        // A-01: silueta de la tabla de reportes.
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <EsqueletoTabla filas={6} columnas={5} />
        </div>
      ) : (
        /* Table */
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr style={{ backgroundColor: "#FAF9FF", borderBottom: "1px solid #E4E4E4" }}>
                  {["Reporte", "Herramienta", "Proyecto", "Reportado por", "Fecha y hora", "Tipo", "Estado", "Acciones"].map((h) => (
                    <th
                      key={h}
                      className="text-left py-3 px-3 text-sm font-semibold first:pl-6 last:pr-4 last:text-right whitespace-nowrap"
                      style={{ color: "#534D59" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <NoResultsState />
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => navigate(`/almacen/reportes/${row.id}/ver`)}
                      className="hover:bg-[#F4F7FB] transition-colors cursor-pointer"
                      style={{ borderBottom: "1px solid #E4E4E4" }}
                    >
                      <td className="py-3 px-3 pl-6 whitespace-nowrap">
                        <span className="text-sm text-gray-500">
                          {row.folio}
                        </span>
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-700">
                          {row.herramienta}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-gray-600 whitespace-nowrap">{row.proyecto}</td>
                      <td className="py-3 px-3 text-gray-600 whitespace-nowrap">{row.responsable}</td>
                      <td className="py-3 px-3 text-gray-500 whitespace-nowrap">{row.fechaHora}</td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        <EstadoBadge estado={row.estado} />
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        <EstatusBadge estatus={row.estatus} />
                      </td>
                      <td className="py-3 px-3 pr-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <ActionsMenu
                          onVer={() => navigate(`/almacen/reportes/${row.id}/ver`)}
                          onCerrar={row.estatus === "Cerrado" ? undefined : () => {
                            setCerrarComentario("");
                            setCerrarTarget(row);
                          }}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {rows.length > PAGE_SIZE && (
            <div style={{ borderTop: "1px solid #E4E4E4" }}>
              <Pagination page={safePage} totalPages={totalPages} onPage={setPage} />
            </div>
          )}
        </div>
      )}

      {cerrarTarget && (
        <CerrarReporteModal
          reporte={cerrarTarget}
          comentario={cerrarComentario}
          onChangeComentario={setCerrarComentario}
          saving={cerrando}
          onChangeDesenlace={setCerrarDesenlace}
          onSubiendoChange={setSubiendoEvidencia}
          puedeCerrar={!!cerrarDesenlace && !subiendoEvidencia}
          onClose={() => { if (!cerrando) { setCerrarTarget(null); setCerrarComentario(""); setCerrarDesenlace(null); } }}
          onConfirm={handleConfirmCerrarRapido}
        />
      )}
    </div>
  );
};

export default ReportesActivos;
